import React, { useRef } from 'react';
import { Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  ALLOWED_ATTACHMENT_MIME_TYPES,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from '../../services/aiChat';

interface AttachmentPickerButtonProps {
  /** Currently queued count — used to enforce MAX_ATTACHMENTS_PER_MESSAGE without opening the picker when full. */
  currentCount: number;
  /** Called with files that pass mime-type + size validation. */
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  title?: string;
}

const ACCEPT_ATTR = ALLOWED_ATTACHMENT_MIME_TYPES.join(',');

export const validateAttachmentFiles = (files: File[], currentCount: number): File[] => {
  const remainingSlots = MAX_ATTACHMENTS_PER_MESSAGE - currentCount;
  if (remainingSlots <= 0) {
    toast.error(`Max ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
    return [];
  }

  const accepted: File[] = [];
  for (const file of files) {
    if (accepted.length >= remainingSlots) {
      toast.error(`Only ${remainingSlots} more file(s) allowed.`);
      break;
    }
    if (!(ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(file.type)) {
      toast.error(`${file.name}: unsupported type (${file.type || 'unknown'}).`);
      continue;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error(`${file.name}: too large (max ${Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024)} MB).`);
      continue;
    }
    accepted.push(file);
  }
  return accepted;
};

const AttachmentPickerButton: React.FC<AttachmentPickerButtonProps> = ({
  currentCount,
  onFilesSelected,
  disabled,
  size = 'md',
  title = 'Attach image or PDF',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isFull = currentCount >= MAX_ATTACHMENTS_PER_MESSAGE;
  const isBlocked = disabled || isFull;

  const dims = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  const iconSize = size === 'sm' ? 14 : 16;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const valid = validateAttachmentFiles(files, currentCount);
    if (valid.length) onFilesSelected(valid);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isBlocked}
        title={isFull ? `Max ${MAX_ATTACHMENTS_PER_MESSAGE} files` : title}
        aria-label={isFull ? `Max ${MAX_ATTACHMENTS_PER_MESSAGE} files` : title}
        className={`${dims} rounded-xl flex items-center justify-center shrink-0 transition-colors duration-150
          focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
          isBlocked
            ? 'text-acero-300 cursor-not-allowed'
            : 'text-acero-500 hover:bg-acero-100 hover:text-navy-950 active:scale-[0.98]'
        }`}
      >
        <Paperclip size={iconSize} />
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        multiple
        onChange={handleChange}
        className="hidden"
      />
    </>
  );
};

export default AttachmentPickerButton;
